import { useState } from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { MatchLEAStatus } from "@/models/mid";

export type MIDsSearchFormState = {
  midType: string;
  midStatus: MatchLEAStatus | "";
  csppid: string;
};

type MidTypeOption = { value: string; label: string };
type MidStatusOption = { value: MatchLEAStatus | ""; label: string };

const MID_TYPE_OPTIONS: MidTypeOption[] = [
  { value: "", label: "All" },
  { value: "External", label: "External" },
  { value: "Internal", label: "Internal" },
];

const MID_STATUS_OPTIONS: MidStatusOption[] = [
  { value: "", label: "All" },
  { value: MatchLEAStatus.UNRESOLVED_ACTIVE_LEA, label: "Unresolved" },
  {
    value: MatchLEAStatus.UNRESOLVED_INACTIVE_LEA,
    label: "Unresolved (Inactive)",
  },
  { value: MatchLEAStatus.RESOLVED_MERGED, label: "Merged" },
  { value: MatchLEAStatus.RESOLVED_REJECTED, label: "Not a Match" },
  { value: MatchLEAStatus.RESOLVED_NO_LONGER_USED, label: "No Longer Used" },
];

const DEFAULT_STATE: MIDsSearchFormState = {
  midType: "",
  midStatus: MatchLEAStatus.UNRESOLVED_ACTIVE_LEA,
  csppid: "",
};

type MIDsSearchFormProps = {
  initialState?: MIDsSearchFormState;
  disabled?: boolean;
  onSearch: (filters: MIDsSearchFormState) => void;
  onClear?: () => void;
};

export function MIDsSearchForm({
  initialState,
  disabled = false,
  onSearch,
  onClear,
}: MIDsSearchFormProps) {
  const [formState, setFormState] = useState<MIDsSearchFormState>(
    initialState ?? DEFAULT_STATE
  );

  const handleChange = <K extends keyof MIDsSearchFormState>(
    key: K,
    value: MIDsSearchFormState[K]
  ) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchClick = () => {
    if (!disabled) onSearch(formState);
  };

  const handleClearClick = () => {
    if (disabled) return;
    setFormState(DEFAULT_STATE);
    onSearch(DEFAULT_STATE);
    onClear?.();
  };

  const selectedMidType =
    MID_TYPE_OPTIONS.find((o) => o.value === formState.midType) ??
    MID_TYPE_OPTIONS[0];

  const selectedMidStatus =
    MID_STATUS_OPTIONS.find((o) => o.value === formState.midStatus) ??
    MID_STATUS_OPTIONS[0];

  return (
    // borderRadius: "inherit" ensures this div clips to whatever border radius
    // the parent card applies, so the white background doesn't bleed over corners.
    <div
      className="mb-0 p-4 bg-white w-100 child-search-form"
      style={{ borderRadius: "inherit" }}
    >
      <div className="row g-3 align-items-end flex-nowrap">
        {/* MID Type */}
        <div className="col">
          {/*
            Not a <label> element: Kendo's DropDownList renders a
            role="combobox" span, not a native form control, so a
            <label for> would be "orphaned". The combobox is given an
            accessible name via ariaLabelledBy instead.
          */}
          <div id="mid-type-label" className="form-label fw-semibold">
            MID Type
          </div>
          <DropDownList
            id="mid-type"
            ariaLabelledBy="mid-type-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={MID_TYPE_OPTIONS}
            textField="label"
            dataItemKey="value"
            value={selectedMidType}
            onChange={(e: DropDownListChangeEvent) =>
              handleChange("midType", (e.value as MidTypeOption).value)
            }
            disabled={disabled}
          />
        </div>

        {/* MID Status */}
        <div className="col">
          <div id="mid-status-label" className="form-label fw-semibold">
            MID Status
          </div>
          <DropDownList
            id="mid-status"
            ariaLabelledBy="mid-status-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={MID_STATUS_OPTIONS}
            textField="label"
            dataItemKey="value"
            value={selectedMidStatus}
            onChange={(e: DropDownListChangeEvent) =>
              handleChange("midStatus", (e.value as MidStatusOption).value)
            }
            disabled={disabled}
          />
        </div>

        {/* CSPPID */}
        <div className="col">
          <label htmlFor="mid-csppid" className="form-label fw-semibold">
            CSPPID
          </label>
          <Input
            id="mid-csppid"
            className="child-search-control"
            style={{ width: "100%" }}
            value={formState.csppid}
            onChange={(e: InputChangeEvent) =>
              handleChange("csppid", e.value ?? "")
            }
            placeholder="Enter CSPPID"
            disabled={disabled}
          />
        </div>

        {/* Buttons */}
        <div className="col-auto d-flex align-items-end gap-2 pb-1">
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSearchClick}
            disabled={disabled}
          >
            <i className="bi bi-search" />
            Search
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClearClick}
            disabled={disabled}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
